import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ChevronDown, ChevronRight, Package, Search } from 'lucide-react'
import { DashboardLayout } from '@/components/layouts/DashboardLayout'
import { OrdersSpaceTabs } from '@/components/features/suppliers/OrdersSpaceTabs'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { getSupplierOrdersBoard, type BoardOrderDto } from '@/lib/api/v2/planning'
import { ApiError } from '@/lib/api/client'
import { cn } from '@/lib/utils'
import {
  LINE_STATUS_CLASS,
  LINE_STATUS_LABEL,
  MODE_LABEL,
  ORDER_STATUS_CLASS,
  ORDER_STATUS_LABEL,
  todayIso,
} from '@/components/features/planning/sidebar/supplier-orders-helpers'
import {
  computeBoardCounts,
  distinctSuppliers,
  filterBoardOrders,
  isOrderOverdue,
  sortBoardOrders,
  type BoardSortKey,
  type BoardStatusFilter,
  type SortDir,
} from '@/lib/supplierOrdersBoard'

const PAGE_SIZE = 25

const frDate = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`

function formatRange(start: string | null, end: string | null): string {
  if (!start || !end) return '—'
  return start === end ? frDate(start) : `${frDate(start)} → ${frDate(end)}`
}

const STATUS_VALUES: BoardStatusFilter[] = ['en-cours', 'Draft', 'Sent', 'Confirmed', 'all']

function initialStatus(raw: string | null): BoardStatusFilter {
  return STATUS_VALUES.find((v) => v === raw) ?? 'en-cours'
}

const STATUS_OPTIONS: Array<{ value: BoardStatusFilter; label: string }> = [
  { value: 'en-cours', label: 'En cours' },
  { value: 'Sent', label: ORDER_STATUS_LABEL.Sent },
  { value: 'Confirmed', label: ORDER_STATUS_LABEL.Confirmed },
  { value: 'Draft', label: ORDER_STATUS_LABEL.Draft },
  { value: 'all', label: 'Toutes' },
]

/**
 * SOLY-PO-001 MS-16 — the org-wide « Commandes fournisseurs » read-only registre. Mirrors the
 * invoices ledger: a filterable, paginated list of every non-closed supplier order, with an
 * expandable per-chantier line detail. Pure monitoring + navigation — no state mutation here
 * (send/confirm/receive stay in the project drawer). No money column: this domain has no amounts.
 */
export default function SupplierOrdersPage() {
  const [orders, setOrders] = useState<BoardOrderDto[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<unknown>(null)

  // SOLY-SUPPLIER-002: the suppliers page links here with ?supplier=<id>&status=en-cours. Read once,
  // as the initial filter — the filters stay local state, the URL is only the way in.
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState<BoardStatusFilter>(() => initialStatus(searchParams.get('status')))
  const [supplierId, setSupplierId] = useState<string>(() => searchParams.get('supplier') ?? 'all')
  const [overdueOnly, setOverdueOnly] = useState(false)
  const [deliveryFrom, setDeliveryFrom] = useState('')
  const [deliveryTo, setDeliveryTo] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<{ key: BoardSortKey; dir: SortDir }>({ key: 'deadline', dir: 'asc' })
  const [page, setPage] = useState(1)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  // Retry handler (event-driven): sets loading synchronously, which is fine outside an effect.
  const load = () => {
    setIsLoading(true)
    setError(null)
    getSupplierOrdersBoard()
      .then((data) => setOrders(data))
      .catch((e) => setError(e))
      .finally(() => setIsLoading(false))
  }

  // Initial fetch: state is set asynchronously (in .then/.finally) so the effect body never
  // updates state synchronously. isLoading starts true, so no synchronous setLoading is needed.
  useEffect(() => {
    let active = true
    getSupplierOrdersBoard()
      .then((data) => {
        if (active) setOrders(data)
      })
      .catch((e) => {
        if (active) setError(e)
      })
      .finally(() => {
        if (active) setIsLoading(false)
      })
    return () => {
      active = false
    }
  }, [])

  // Debounced search — reset to page 1 once the user pauses.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput)
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchInput])

  const today = todayIso()

  const suppliers = useMemo(() => distinctSuppliers(orders), [orders])

  const filtered = useMemo(
    () => filterBoardOrders(orders, { status, supplierId, search, overdueOnly, deliveryFrom, deliveryTo }, today),
    [orders, status, supplierId, search, overdueOnly, deliveryFrom, deliveryTo, today],
  )
  const sorted = useMemo(() => sortBoardOrders(filtered, sort.key, sort.dir), [filtered, sort])
  const counts = useMemo(() => computeBoardCounts(filtered, today), [filtered, today])

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE))
  const currentPage = Math.min(page, totalPages)
  const pageItems = sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
  const start = sorted.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1
  const end = Math.min(currentPage * PAGE_SIZE, sorted.length)

  const hasActiveFilters =
    status !== 'en-cours' || supplierId !== 'all' || overdueOnly || !!deliveryFrom || !!deliveryTo || !!search

  const resetPage = () => setPage(1)

  const toggleSort = (key: BoardSortKey) => {
    resetPage()
    setSort((prev) => (prev.key !== key ? { key, dir: 'asc' } : { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }))
  }
  const sortIndicator = (key: BoardSortKey) => (sort.key !== key ? '' : sort.dir === 'asc' ? ' ↑' : ' ↓')

  const toggleExpanded = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  return (
    <DashboardLayout>
      <div className="space-y-4">
        <div>
          <div className="flex items-center gap-2">
            <Package className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-bold">Commandes fournisseurs</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Toutes les commandes fournisseurs en cours de votre organisation — où ça en est, ce qui est en retard
          </p>
        </div>

        <OrdersSpaceTabs active="orders" />

        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Fournisseur, n° ou chantier…"
              className="pl-8"
              data-testid="supplier-orders-search-input"
            />
          </div>

          <div className="space-y-1">
            <span className="text-xs text-muted-foreground">Statut</span>
            <Select
              value={status}
              onValueChange={(v) => {
                setStatus(v as BoardStatusFilter)
                resetPage()
              }}
            >
              <SelectTrigger className="w-[150px]" data-testid="supplier-orders-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <span className="text-xs text-muted-foreground">Fournisseur</span>
            <Select
              value={supplierId}
              onValueChange={(v) => {
                setSupplierId(v)
                resetPage()
              }}
            >
              <SelectTrigger className="w-[190px]" data-testid="supplier-orders-supplier-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les fournisseurs</SelectItem>
                {suppliers.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <span className="text-xs text-muted-foreground">Livraison du</span>
            <Input
              type="date"
              value={deliveryFrom}
              onChange={(e) => {
                setDeliveryFrom(e.target.value)
                resetPage()
              }}
              className="w-[160px]"
              data-testid="supplier-orders-from-input"
            />
          </div>

          <div className="space-y-1">
            <span className="text-xs text-muted-foreground">au</span>
            <Input
              type="date"
              value={deliveryTo}
              onChange={(e) => {
                setDeliveryTo(e.target.value)
                resetPage()
              }}
              className="w-[160px]"
              data-testid="supplier-orders-to-input"
            />
          </div>

          <Button
            type="button"
            variant={overdueOnly ? 'default' : 'outline'}
            size="sm"
            className="h-9"
            onClick={() => {
              setOverdueOnly((v) => !v)
              resetPage()
            }}
            data-testid="supplier-orders-overdue-toggle"
          >
            En retard
          </Button>

          <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
            <span className="text-xs text-muted-foreground">Trier par:</span>
            <Button type="button" variant={sort.key === 'deadline' ? 'default' : 'outline'} size="sm" className="h-7 text-xs" onClick={() => toggleSort('deadline')} data-testid="supplier-orders-sort-deadline-btn">
              Échéance{sortIndicator('deadline')}
            </Button>
            <Button type="button" variant={sort.key === 'sent' ? 'default' : 'outline'} size="sm" className="h-7 text-xs" onClick={() => toggleSort('sent')} data-testid="supplier-orders-sort-sent-btn">
              Envoi{sortIndicator('sent')}
            </Button>
            <Button type="button" variant={sort.key === 'supplier' ? 'default' : 'outline'} size="sm" className="h-7 text-xs" onClick={() => toggleSort('supplier')} data-testid="supplier-orders-sort-supplier-btn">
              Fournisseur{sortIndicator('supplier')}
            </Button>
          </div>
        </div>

        {/* Summary band — operational counts, no money (this domain has no amounts) */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 rounded-lg border bg-background px-4 py-3 text-sm" data-testid="supplier-orders-summary">
          <div>
            <span className="font-semibold">{counts.total}</span>{' '}
            <span className="text-muted-foreground">commande{counts.total > 1 ? 's' : ''}</span>
          </div>
          <div>
            <span className="font-semibold tabular-nums">{counts.awaitingConfirmation}</span>{' '}
            <span className="text-muted-foreground">en attente de confirmation</span>
          </div>
          <div>
            <span className={cn('font-semibold tabular-nums', counts.overdue > 0 && 'text-red-600')}>{counts.overdue}</span>{' '}
            <span className="text-muted-foreground">en retard</span>
          </div>
          {hasActiveFilters && <span className="text-xs text-muted-foreground">sur la sélection filtrée</span>}
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between gap-3">
          <div className="text-xs text-muted-foreground" data-testid="supplier-orders-range-label">
            Affichage {start}-{end} sur {sorted.length}
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" className="h-8" disabled={isLoading || currentPage <= 1} onClick={() => setPage(currentPage - 1)} data-testid="supplier-orders-prev-btn">
              Précédent
            </Button>
            <div className="text-xs text-muted-foreground">Page {currentPage}/{totalPages}</div>
            <Button type="button" variant="outline" size="sm" className="h-8" disabled={isLoading || currentPage >= totalPages} onClick={() => setPage(currentPage + 1)} data-testid="supplier-orders-next-btn">
              Suivant
            </Button>
          </div>
        </div>

        {/* List */}
        <div className="relative">
          {isLoading && (
            <div className="rounded-lg border bg-background py-10 text-center text-sm text-muted-foreground" data-testid="supplier-orders-loading">
              Chargement…
            </div>
          )}

          {!isLoading && error != null ? (
            <div className="rounded-lg border bg-background py-10 text-center text-sm">
              {error instanceof ApiError && error.status === 403 ? (
                <p className="text-muted-foreground">Vous n'avez pas la permission de consulter les commandes.</p>
              ) : (
                <>
                  <p className="text-destructive">Impossible de charger les commandes.</p>
                  <Button type="button" variant="outline" size="sm" className="mt-3" onClick={load} data-testid="supplier-orders-retry-btn">
                    Réessayer
                  </Button>
                </>
              )}
            </div>
          ) : !isLoading && pageItems.length === 0 ? (
            <div className="rounded-lg border bg-background py-10 text-center text-sm text-muted-foreground" data-testid="supplier-orders-empty">
              {hasActiveFilters ? 'Aucune commande ne correspond aux filtres.' : 'Aucune commande fournisseur en cours.'}
            </div>
          ) : (
            !isLoading && (
              <div className="divide-y rounded-lg border bg-background">
                {pageItems.map((order) => (
                  <OrderRow key={order.id} order={order} today={today} expanded={expanded.has(order.id)} onToggle={() => toggleExpanded(order.id)} />
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </DashboardLayout>
  )
}

/** One order row + its expandable per-chantier line detail. Hoisted to module level (a nested
 *  component type would remount the list on every expand/filter change). */
function OrderRow({ order, today, expanded, onToggle }: { order: BoardOrderDto; today: string; expanded: boolean; onToggle: () => void }) {
  const overdue = isOrderOverdue(order, today)
  const linesByProject = useMemo(() => {
    const map = new Map<string, { clientName: string; lines: BoardOrderDto['lines'] }>()
    for (const line of order.lines) {
      const bucket = map.get(line.projectId) ?? { clientName: line.clientName, lines: [] }
      bucket.lines.push(line)
      map.set(line.projectId, bucket)
    }
    return [...map.entries()].map(([projectId, v]) => ({ projectId, ...v }))
  }, [order.lines])

  return (
    <div data-testid="supplier-orders-row">
      <div className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-start gap-2">
          <button
            type="button"
            onClick={onToggle}
            disabled={order.lines.length === 0}
            aria-label={expanded ? 'Réduire' : 'Voir les lignes'}
            className="mt-0.5 rounded p-0.5 text-muted-foreground hover:bg-muted disabled:opacity-30"
            data-testid="supplier-orders-row-toggle"
          >
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{order.supplierName}</span>
              <Badge className={cn('rounded px-1.5 py-0.5 text-[10px] font-medium border-0', ORDER_STATUS_CLASS[order.status])}>
                {ORDER_STATUS_LABEL[order.status]}
              </Badge>
              {order.supplierOrderNumber && <span className="text-xs text-muted-foreground">n° {order.supplierOrderNumber}</span>}
            </div>
            <div className="flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
              <span>{order.totalLineCount} art. · {order.projects.length} chantier{order.projects.length > 1 ? 's' : ''}</span>
              {order.mode && (
                <>
                  <span>·</span>
                  <span>{MODE_LABEL[order.mode]}</span>
                </>
              )}
              {order.sentDate && (
                <>
                  <span>·</span>
                  <span>envoyée le {frDate(order.sentDate)}</span>
                </>
              )}
            </div>
            <div className="flex flex-wrap gap-1">
              {order.projects.map((p) => (
                <Link key={p.projectId} to={`/planning/projects/${p.projectId}`} className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-foreground hover:bg-muted-foreground/20" data-testid="supplier-orders-project-chip">
                  {p.clientName}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4 sm:gap-6">
          <div className="text-right text-sm">
            <div className="text-xs text-muted-foreground">Livraison</div>
            <div className="tabular-nums">
              {formatRange(order.deliveryStart, order.deliveryEnd)}
              {order.deliveryStart && !order.deliveryConfirmed && <span className="text-xs text-muted-foreground"> (souhaitée)</span>}
            </div>
          </div>
          <div className="text-right text-sm">
            <div className="text-xs text-muted-foreground">Échéance</div>
            {order.nextDeadline ? (
              <div className={cn('tabular-nums', overdue && 'font-medium text-red-600')}>
                {order.nextDeadline.kind === 'confirmation' ? 'Confirmation' : 'Livraison'} · {frDate(order.nextDeadline.date)}
              </div>
            ) : (
              <div className="text-muted-foreground">—</div>
            )}
          </div>
          <div className="w-16 text-right text-sm">
            <div className="text-xs text-muted-foreground">Reçues</div>
            <div className="tabular-nums">{order.receivedLineCount}/{order.totalLineCount}</div>
          </div>
        </div>
      </div>

      {expanded && order.lines.length > 0 && (
        <div className="space-y-3 border-t bg-muted/20 px-4 py-3" data-testid="supplier-orders-row-detail">
          {linesByProject.map((group) => (
            <div key={group.projectId}>
              <Link to={`/planning/projects/${group.projectId}`} className="text-xs font-semibold hover:underline" data-testid="supplier-orders-detail-project">
                {group.clientName}
              </Link>
              <div className="mt-1 space-y-1">
                {group.lines.map((line) => (
                  <div key={line.id} className="flex items-center gap-2 text-sm" data-testid="supplier-orders-detail-line">
                    <Badge className={cn('rounded px-1.5 py-0.5 text-[10px] font-medium border-0', LINE_STATUS_CLASS[line.lineStatus])}>
                      {LINE_STATUS_LABEL[line.lineStatus]}
                    </Badge>
                    <span className="min-w-0 flex-1 truncate">{line.label}</span>
                    <span className="flex-none text-xs text-muted-foreground tabular-nums">
                      {formatRange(line.deliveryStart, line.deliveryEnd)}
                      {line.deliveryStart && !line.deliveryConfirmed && <span> (souhaitée)</span>}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
